import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoBannerCst from "@components/InfoBannerCst";
import { useParams } from "react-router-dom";
import { Search } from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import ListItemCardCst from "@components/ListItemCardCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { WizardStepProps } from "@components/WizardCst/types";
import { UserWizardData } from "../types";
import { StepContainer } from "../components/StepLayout";

const PAGE_SIZE = 20;

const AddToGroupsStep: React.FC<WizardStepProps<UserWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("user-wizard-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const [search, setSearch] = useState("");

  const listUrl = corporateId
    ? `${ServicesURL.staticGroups}/static?id_corporate=${corporateId}`
    : null;

  const { items, isLoading } = usePaginatedList(listUrl, {
    page: 1,
    pageSize: PAGE_SIZE,
    search: search || undefined,
  });

  const selected = useMemo(
    () => new Set(data.staticGroupIds),
    [data.staticGroupIds],
  );

  const toggle = (groupId: string | number, next: boolean) => {
    setData((prev) => {
      const set = new Set(prev.staticGroupIds);
      if (next) set.add(groupId);
      else set.delete(groupId);
      return { staticGroupIds: Array.from(set) };
    });
  };

  return (
    <StepContainer>
      <InfoBannerCst>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 700, color: "inherit" }}
        >
          {t("groups.noticeTitle")}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "inherit" }}>
          {t("groups.noticeText")}
        </Typography>
      </InfoBannerCst>

      <Box
        sx={{
          "& .MuiFormControl-root": {
            minWidth: "0 !important",
            width: "100% !important",
          },
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        }}
      >
        <InputCst
          id="user-groups-search"
          label={t("groups.search")}
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
          endAdornment={
            <Search size={16} color={theme.palette.text.tertiary} />
          }
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            color: theme.palette.text.primary,
          }}
        >
          {t("groups.empty")}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((group: any) => (
            <ListItemCardCst
              key={group.id}
              title={group.name || "-"}
              subtitle={{ id: group.id }}
              badges={[]}
              actions={[]}
              selectable
              selected={selected.has(group.id)}
              onSelect={(next) => toggle(group.id, next)}
            />
          ))}
        </Box>
      )}
    </StepContainer>
  );
};

export default AddToGroupsStep;
